/**
 * Фразы-ссылки на фото в промпте юзера (пилюли, владелец 2026-08-23).
 *
 * После применения карточки {photoN} уже подставлены позиционными фразами
 * («the first reference image» — см. substitutePhotoSlots): текст в состоянии
 * промпта и есть то, что уходит в модель. Пилюли — только ОТОБРАЖЕНИЕ этих
 * фраз: редактор узнаёт их в тексте и рисует чипом с превью фото по наведению.
 * В модель уходит тот же текст, что и раньше, — механика генерации не меняется.
 *
 * Ранг фразы («first» → 1) = позиция файла в referenceImages = N-й слот
 * активного набора по возрастанию (mirrorSlots в useGenerateUploads собирает
 * файлы в этом же порядке — фразы и файлы не разъезжаются).
 */

/**
 * Ядро (фразы, разбор текста на пилюли, подписи токенов провайдера) — общий модуль
 * prompt-vars/phrases (мастер в shared/prompt-vars, то же в админке и мастерской).
 * Здесь остаётся только то, что нужно сайту: применимость фраз к модели и переписывание
 * текста при смене модели.
 */
import { promptRefStyleOf, type PromptKind, type PromptRefStyle } from '../prompt-vars'
import {
	AUDIO_REF_PHRASE,
	CHARACTER_PHRASE,
	FIRST_FRAME_PHRASES,
	LAST_FRAME_PHRASE,
	PHOTO_PHRASES,
	PHRASE_RE,
	VIDEO_REF_PHRASE,
	hasPillPhrases,
	phraseForRank,
	refTokenLabel,
	splitPromptByPhrases,
	tokenForPhrase,
	type PillToken,
} from '../prompt-vars/phrases'
import { hasFrameRefModes } from './frameRefModes'

export type { PromptKind, PromptRefStyle, PillToken }
export {
	AUDIO_REF_PHRASE,
	CHARACTER_PHRASE,
	FIRST_FRAME_PHRASES,
	LAST_FRAME_PHRASE,
	PHOTO_PHRASES,
	VIDEO_REF_PHRASE,
	hasPillPhrases,
	phraseForRank,
	promptRefStyleOf,
	refTokenLabel,
	splitPromptByPhrases,
	tokenForPhrase,
}

export interface PillPhraseCaps {
	photo: boolean
	first_frame: boolean
	last_frame: boolean
	character: boolean
	video_ref: boolean
	audio_ref: boolean
}

/**
 * Какие фразы-пилюли ПРИМЕНИМЫ к модели — из её флагов возможностей (те же,
 * которыми рисуются слоты загрузки). Универсально: новая модель получает
 * поведение из флагов; единственное имя — Seedance (runtime-режим «Кадры»).
 */
export function pillPhraseCaps(
	model: { id?: string; backendModel?: string; maxImages?: number; supportsMultipleImages?: boolean; supportsImageInput?: boolean; requiresImage?: boolean; supportsEndImage?: boolean; supportsReferenceVideo?: boolean; supportsReferenceAudio?: boolean } | null | undefined,
	seedanceMode?: string,
	hasCardSlots?: boolean,
	/** Готовый расчёт «модель берёт несколько фото» (maxImages > 1 из рантайм-модели,
	 *  как в useGenerateUploads) — статический флаг у обогащённых моделей пуст. */
	multiOverride?: boolean,
): PillPhraseCaps {
	const m = model || {}
	const seedanceFrames = hasFrameRefModes(m) && (seedanceMode ?? 'frames') === 'frames'
	const multi = (multiOverride ?? (!!m.supportsMultipleImages || (m.maxImages ?? 1) > 1)) && !seedanceFrames
	return {
		photo: !!hasCardSlots || multi,
		first_frame: (!!m.requiresImage || !!m.supportsImageInput) && !multi,
		last_frame: !!m.supportsEndImage || seedanceFrames,
		character: true,
		video_ref: !!m.supportsReferenceVideo && !seedanceFrames,
		audio_ref: !!m.supportsReferenceAudio && !seedanceFrames,
	}
}

/**
 * Вырезать из ОТПРАВЛЯЕМОГО промпта фразы входов, которые текущая модель не
 * потребляет (в состоянии текст остаётся целиком — пилюли вернутся при возврате
 * к модели; см. GenerateInputBar). Подчищает задвоенные пробелы после вырезки.
 */
export function stripInactivePillPhrases(text: string, caps: PillPhraseCaps): string {
	if (!text) return text
	let changed = false
	const out = text.replace(PHRASE_RE, (m) => {
		const tok = tokenForPhrase(m)
		// Токены шаблона ({variant}, {value}) в тексте сайта не встречаются — их вид не гасим.
		const allowed = tok ? (caps as Partial<Record<PillToken['kind'], boolean>>)[tok.kind] : true
		if (!tok || allowed !== false) return m
		changed = true
		return ''
	})
	if (!changed) return text
	return out.replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+([,.!?;:])/g, '$1').replace(/^[ \t]+/gm, '').replace(/[ \t]+$/gm, '')
}

/**
 * Переключение картинка↔видео: переписать известные фразы под новый тип модели
 * («the attached photo» ↔ «the first frame image»); старые фразы фото («the
 * first image», «the first reference image») приводятся к «Image N».
 * Незнакомый текст не трогаем.
 */
export function rewritePromptKind(text: string, kind: PromptKind): string {
	if (!text) return text
	return text.replace(PHRASE_RE, (m) => {
		const tok = tokenForPhrase(m)
		if (!tok) return m
		if (tok.kind === 'photo' && tok.rank) return PHOTO_PHRASES[kind][tok.rank - 1] ?? m
		if (tok.kind === 'first_frame') return FIRST_FRAME_PHRASES[kind]
		return m
	})
}
